'use client';

/**
 * IMPROVE — one-focus-at-a-time control room (Boss, 2026-07-08).
 *
 * GALLERY: crystal proposal panes on the v4 carousel arc — flick left/right
 * (same physics as the hangar), business filter pills, ⚡ AUTO batch-approve.
 * FOCUS (pinch a card): one proposal fills the stage — WHY pane (rationale +
 * evidence), PREVIEW pane (SERP before/after mock + live page iframe), DECIDE
 * pane (projected impact + big APPROVE / REJECT) + ◈ ASSISTANT (real gateway
 * session) to interrogate the proposal.
 *
 * Every number is real: /api/improve/recommendations + PATCH :id
 * (approve/reject). Execution stays with the existing executor pipeline.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useCarousel } from '@/future/physics/useCarousel';
import { EmptyState, mono, display } from '@/future/primitives';
import { ImproveAssistant } from '@/components/improve/ImproveAssistant';

const CY = '#9ef0ff';
const fmtPct = (v: number) => `${(v * 100).toFixed(2)}%`;
const fmtN = (n: number) => n >= 1000 ? `${(n / 1000).toFixed(1)}K` : String(Math.round(n));

/* ── generic accessors across rec types (seo_title / content_gap / ab_promote) ── */
const ev0 = (r: any) => (Array.isArray(r.evidence) ? r.evidence[0] : r.evidence) || {};
const rationaleOf = (r: any) => r.proposal?.rationale || r.proposal?.hypothesis || '';
const pageOf = (r: any) => r.proposal?.page || ev0(r).page || ev0(r).homepage?.page || (r.business ? `https://${r.business}/` : '');
const beforeAfter = (r: any): { before?: { title: string; meta?: string }; after?: { title: string; meta?: string } } => {
  const p = r.proposal || {};
  if (p.current_title || p.new_title) return { before: { title: p.current_title }, after: { title: p.new_title, meta: p.new_meta } };
  if (p.variant_a || p.variant_b) return { before: { title: p.variant_a?.title, meta: p.variant_a?.headline }, after: { title: p.variant_b?.title, meta: p.variant_b?.headline } };
  return {};
};
const kpisOf = (r: any): { l: string; v: string }[] => {
  const e = ev0(r);
  const out: { l: string; v: string }[] = [];
  if (e.missed_clicks != null) out.push({ l: 'MISSED CLICKS/MO', v: fmtN(e.missed_clicks) });
  if (e.impressions != null) out.push({ l: 'IMPRESSIONS', v: fmtN(e.impressions) });
  if (e.homepage?.impressions != null) out.push({ l: 'IMPRESSIONS', v: fmtN(e.homepage.impressions) });
  if (e.avg_position != null) out.push({ l: 'POSITION', v: e.avg_position.toFixed(1) });
  if (e.ctr != null) out.push({ l: 'CTR', v: fmtPct(e.ctr) });
  if (!out.length && e.top_pages?.[0]) out.push({ l: 'BEST POSITION', v: Number(e.top_pages[0].position).toFixed(1) }, { l: 'IMPRESSIONS', v: fmtN(e.top_pages.reduce((s: number, p: any) => s + (p.impressions || 0), 0)) });
  return out.slice(0, 3);
};
const TYPE_LABEL: Record<string, string> = { seo_title: 'TITLE REWRITE', content_gap: 'CONTENT GAP', ab_promote: 'A/B TEST' };
const ST_COLOR: Record<string, string> = { proposed: '#ffc36b', approved: '#9ef0ff', done: '#5df2b4', applied: '#5df2b4', executed: '#5df2b4', rejected: '#ff7d8a' };

/* ── v4 arc math (spec verbatim), centered in the surface area ──── */
const AS = [1.15, .88, .66, .52, .44];
const AO = [1, .85, .45, .14, .05]; // Boss 2026-07-10: reverted one-card-only fade — he wants to SEE the side cards
function paneStyle(off: number, scrub: boolean): React.CSSProperties {
  const ad = Math.abs(off);
  const deg = ad <= 2 ? ad * 38 : 76 + (ad - 2) * 7;
  const th = Math.sign(off) * deg * Math.PI / 180;
  const az = Math.min(ad, 4), a0 = Math.floor(az), a1 = Math.min(a0 + 1, 4), at = az - a0;
  const sc = AS[a0] + (AS[a1] - AS[a0]) * at;
  const op = AO[a0] + (AO[a1] - AO[a0]) * at;
  return {
    position: 'absolute', left: `calc(50% + ${Math.round(820 * Math.sin(th))}px - 190px)`,
    top: 150 + Math.round(120 * (Math.cos(th) - 1)), width: 380, height: 470, /* clear the wrapped pill rows (Boss 2026-07-10) */
    transformOrigin: '50% 100%',
    transform: `perspective(1800px) rotateY(${Math.sign(off) * Math.min(ad, 2) * -12}deg) scale(${sc})`,
    opacity: op, zIndex: Math.round(40 - ad * 10),
    transition: scrub ? 'opacity .3s' : 'left .55s cubic-bezier(.2,.75,.25,1),top .55s cubic-bezier(.2,.75,.25,1),transform .55s cubic-bezier(.2,.75,.25,1),opacity .45s ease-out',
  };
}

/* SERP result mock — the "what it will look like on Google" preview */
function Serp({ label, url, title, meta, glow }: { label: string; url: string; title?: string; meta?: string; glow?: boolean }) {
  return (
    <div style={{ borderRadius: 10, padding: '12px 16px', border: `1px solid ${glow ? 'rgba(93,242,180,.4)' : 'rgba(125,232,255,.16)'}`, background: glow ? 'rgba(93,242,180,.04)' : 'rgba(4,10,18,.5)', boxShadow: glow ? '0 0 30px -10px rgba(93,242,180,.45)' : 'none' }}>
      <div style={{ ...mono(14, '.26em'), color: glow ? '#5df2b4' : '#56788c', marginBottom: 8 }}>{label}</div>
      <div style={{ fontFamily: 'arial, sans-serif', fontSize: 22, color: '#bdc1c6', marginBottom: 2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{url}</div>
      <div style={{ fontFamily: 'arial, sans-serif', fontSize: 32, color: '#8ab4f8', lineHeight: 1.3, marginBottom: 4 }}>{title || '—'}</div>
      {meta && <div style={{ fontFamily: 'arial, sans-serif', fontSize: 25, color: '#9aa0a6', lineHeight: 1.45 }}>{meta}</div>}
    </div>
  );
}

export function ImproveSurface({ scope, exclude, scopeName }: { scope?: string[]; exclude?: string[]; scopeName?: string } = {}) {
  const [recs, setRecs] = useState<any[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [biz, setBiz] = useState('');
  const [focusId, setFocusId] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [auto, setAuto] = useState<{ done: number; total: number } | null>(null);
  const [confirmAuto, setConfirmAuto] = useState(false);
  const [iaOpen, setIaOpen] = useState(false);

  const [allSites, setAllSites] = useState<string[]>([]); // every LIVE factory domain (Boss 2026-07-16)

  const load = useCallback(() => {
    fetch('/api/improve/recommendations').then(r => r.json())
      .then(d => { setRecs(d.recommendations || d.recs || []); setLoaded(true); }).catch(() => setLoaded(true));
  }, []);
  useEffect(() => { load(); const t = setInterval(load, 60_000); return () => clearInterval(t); }, [load]);
  // Full live-site roster from the same source Classic UI uses (portfolio), so the
  // picker lists EVERY live site — not only the ones that already have cards.
  useEffect(() => {
    fetch('/api/portfolio').then(r => r.json()).then((d: any) => {
      const arr = Array.isArray(d) ? d : (d.sites || []);
      const doms = arr.filter((s: any) => s.deployed && s.domain).map((s: any) => String(s.domain));
      setAllSites(doms);
    }).catch(() => {});
  }, []);

  // Project-scoped Improve (Boss 2026-07-13): the Olga card = "improve everything for Olga".
  // When a scope (set of the project's domains) is passed, the whole surface only ever sees
  // that project's recommendations — the business pills become Olga's own sites, nothing else.
  const inScope = (r: any) => {
    const b = r.business || '';
    if (scope) return scope.some(d => b.includes(d));
    // factory view: show everything EXCEPT domains owned by another project
    if (exclude && exclude.some(d => b.includes(d))) return false;
    return true;
  };
  const scoped = recs.filter(inScope);
  // Union of (sites-with-cards) + (every live factory site), scoped/excluded the same way.
  // Dedupe by a loose key (dots/dashes/case ignored) so 'porta.potty.ca' and
  // 'porta-potty.ca' collapse to one — preferring the exact string a card uses.
  const normKey = (d: string) => d.toLowerCase().replace(/^www\./, '').replace(/[-.]/g, '');
  const cardBiz = [...new Set(scoped.map(r => r.business).filter(Boolean))] as string[];
  const liveScoped = allSites.filter(d => inScope({ business: d }));
  const byKey = new Map<string, string>();
  for (const d of liveScoped) byKey.set(normKey(d), d);
  for (const d of cardBiz) byKey.set(normKey(d), d); // card's exact string wins (so filtering matches)
  const businesses = [...byKey.values()].sort();
  const rank = (s: string) => s === 'proposed' ? 0 : s === 'approved' ? 1 : 2;
  const pool = (biz ? scoped.filter(r => r.business === biz) : scoped).slice().sort((a, b) => rank(a.status) - rank(b.status) || (b.evidence?.[0]?.missed_clicks || 0) - (a.evidence?.[0]?.missed_clicks || 0));
  const proposed = pool.filter(r => r.status === 'proposed');
  // card count per site (all statuses) + grand total, for the picker labels
  const counts: Record<string, number> = {};
  for (const r of scoped) counts[r.business] = (counts[r.business] || 0) + 1;
  const totalCards = scoped.length;

  const decide = async (rec: any, action: 'approve' | 'reject') => {
    if (busy) return;
    setBusy(rec.id);
    try {
      await fetch(`/api/improve/recommendations/${rec.id}`, {
        method: 'PATCH', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action }),
      });
      load();
    } finally { setBusy(null); }
  };

  const runAuto = async () => {
    setConfirmAuto(false);
    const targets = proposed;
    setAuto({ done: 0, total: targets.length });
    for (let i = 0; i < targets.length; i++) {
      try {
        await fetch(`/api/improve/recommendations/${targets[i].id}`, {
          method: 'PATCH', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ action: 'approve', note: 'auto-approved batch' }),
        });
      } catch {}
      setAuto({ done: i + 1, total: targets.length });
    }
    load();
    setTimeout(() => setAuto(null), 2500);
  };

  const focus = focusId ? recs.find(r => r.id === focusId) : null;

  return (
    <div className="absolute inset-0" style={{ top: 190, bottom: 84 }}>
      {focus
        ? <FocusView rec={focus} busy={busy === focus.id} onDecide={decide} onBack={() => setFocusId(null)} iaOpen={iaOpen} setIaOpen={setIaOpen} />
        : <Gallery pool={pool} loaded={loaded} businesses={businesses} biz={biz} setBiz={setBiz}
            counts={counts} totalCards={totalCards}
            proposedCount={proposed.length} auto={auto} confirmAuto={confirmAuto} setConfirmAuto={setConfirmAuto}
            runAuto={runAuto} onFocus={(id: string) => setFocusId(id)} iaOpen={iaOpen} setIaOpen={setIaOpen} />}
      {/* ◈ IMPROVE ASSISTANT — summoned holo-slab (iaStyle verbatim), available in both modes */}
      <div style={{
        position: 'fixed', right: 36, bottom: 130, width: 560, height: 640, maxHeight: 'calc(100vh - 180px)', zIndex: 66,
        borderRadius: 16, border: '1px solid rgba(125,232,255,.4)',
        background: 'linear-gradient(170deg,rgba(40,140,180,.12),rgba(4,10,18,.95) 55%)',
        boxShadow: '0 40px 100px -24px rgba(0,0,0,.9),0 0 70px -16px rgba(0,240,255,.35)',
        padding: 18, overflow: 'hidden', display: 'flex', flexDirection: 'column', gap: 12,
        opacity: iaOpen ? 1 : 0, transform: iaOpen ? 'translateY(0) scale(1)' : 'translateY(40px) scale(.92)',
        pointerEvents: iaOpen ? 'auto' : 'none', visibility: iaOpen ? 'visible' : 'hidden',
        transition: 'opacity .35s,transform .35s cubic-bezier(.25,.9,.3,1),visibility .35s',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ ...mono(18, '.24em'), color: CY, textShadow: '0 0 10px rgba(0,240,255,.4)' }}>◈ IMPROVE ASSISTANT</span>
          <span style={{ ...mono(14, '.18em'), color: '#49677a' }}>{focus ? `${focus.business} · ${TYPE_LABEL[focus.type] || focus.type}` : 'SEO PIPELINE'}</span>
          <span style={{ flex: 1 }} />
          <button onMouseUp={() => setIaOpen(false)} aria-label="Close assistant" style={{ ...mono(18), color: '#56788c', background: 'transparent', border: 'none' }}>✕</button>
        </div>
        <div className="flex-1 min-h-0">
          {iaOpen && <ImproveAssistant overview={null} recs={focus ? [focus] : recs.slice(0, 30)} />}
        </div>
      </div>
    </div>
  );
}

/* ── GALLERY — the flickable crystal panes ──────────────────────── */
function Gallery({ pool, loaded, businesses, biz, setBiz, counts, totalCards, proposedCount, auto, confirmAuto, setConfirmAuto, runAuto, onFocus, iaOpen, setIaOpen }: any) {
  const car = useCarousel(Math.max(pool.length, 1), (i) => pool[i] && onFocus(pool[i].id));
  const host = useRef<HTMLDivElement>(null);
  const [pickOpen, setPickOpen] = useState(false);
  const label = (b: string) => b.replace(/\.(ca|fr|com|film)$/, '').toUpperCase();
  useEffect(() => {
    const el = host.current;
    if (!el) return;
    const wheel = (e: WheelEvent) => car.onWheel(e);
    el.addEventListener('wheel', wheel, { passive: false });
    return () => el.removeEventListener('wheel', wheel);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [car.onWheel]);

  const pill = (active: boolean): React.CSSProperties => active
    ? { padding: '6px 14px', borderRadius: 999, color: '#04121a', background: 'linear-gradient(180deg,#a8eeff,#5fd4ef)', boxShadow: '0 0 16px rgba(0,240,255,.4)', ...mono(16, '.18em'), border: 'none', transition: 'all .3s', whiteSpace: 'nowrap' }
    : { padding: '6px 14px', borderRadius: 999, color: '#56788c', border: '1px solid rgba(125,232,255,.16)', background: 'transparent', ...mono(16, '.18em'), transition: 'all .3s', whiteSpace: 'nowrap' };

  return (
    <div ref={host} className="absolute inset-0" /* clearances: chrome top + legends bottom (Boss 2026-07-10) */
      onPointerDown={car.onPointerDown} onPointerMove={car.onPointerMove} onPointerUp={(e) => car.onPointerUp(e)}>
      {/* business filter — compact dropdown (Boss 2026-07-16): "ALL · 80 ▾", open to pick one */}
      <div className="absolute top-0" style={{ left: 180, zIndex: 55 }}>
        <button onMouseUp={() => setPickOpen(o => !o)} data-lock="PICK BUSINESS"
          style={{ ...pill(!!biz), display: 'flex', alignItems: 'center', gap: 10, minWidth: 280, justifyContent: 'space-between' }}>
          <span>{biz ? `${label(biz)} · ${counts[biz] || 0} IMPROVEMENT${(counts[biz] || 0) === 1 ? '' : 'S'}` : `ALL SITES · ${totalCards} IMPROVEMENTS`}</span>
          <span style={{ transform: pickOpen ? 'rotate(180deg)' : 'none', transition: 'transform .25s', fontSize: 12 }}>▾</span>
        </button>
        {pickOpen && (
          <div style={{
            position: 'absolute', top: 44, left: 0, minWidth: 240, maxHeight: 420, overflowY: 'auto',
            borderRadius: 12, padding: 6, zIndex: 60,
            background: 'linear-gradient(170deg,rgba(20,60,80,.96),rgba(4,10,18,.98) 60%)',
            border: '1px solid rgba(125,232,255,.3)', boxShadow: '0 30px 80px -20px rgba(0,0,0,.9),0 0 50px -18px rgba(0,240,255,.35)',
          }}>
            <button onMouseUp={() => { setBiz(''); setPickOpen(false); }} data-lock="ALL"
              style={{ display: 'flex', justifyContent: 'space-between', gap: 16, width: '100%', textAlign: 'left', padding: '9px 14px', borderRadius: 8, background: !biz ? 'rgba(125,232,255,.12)' : 'transparent', border: 'none', color: !biz ? '#aef0ff' : '#7fa9bd', ...mono(15, '.14em') }}>
              <span>ALL LIVE SITES</span><span style={{ color: '#5fd4ef' }}>{totalCards}</span>
            </button>
            {businesses.map((b: string) => {
              const n = counts[b] || 0;
              return (
              <button key={b} onMouseUp={() => { setBiz(b); setPickOpen(false); }} data-lock={label(b)}
                style={{ display: 'flex', justifyContent: 'space-between', gap: 16, width: '100%', textAlign: 'left', padding: '9px 14px', borderRadius: 8, background: biz === b ? 'rgba(125,232,255,.12)' : 'transparent', border: 'none', color: biz === b ? '#aef0ff' : (n ? '#7fa9bd' : '#3f5665'), ...mono(15, '.14em') }}>
                <span>{label(b)}</span><span style={{ color: n ? '#5fd4ef' : '#3f5665' }}>{n}</span>
              </button>
            );})}
          </div>
        )}
      </div>
      {/* ⚡ AUTO + assistant chips */}
      <div className="absolute right-2 top-0 flex items-center" style={{ gap: 10, zIndex: 50 }}>
        <button onMouseUp={() => setIaOpen(!iaOpen)} style={pill(iaOpen)} data-lock="ASSISTANT">◈ ASSISTANT</button>
        {auto ? (
          <span style={{ ...mono(16, '.18em'), color: '#5df2b4', padding: '6px 14px', borderRadius: 999, border: '1px solid rgba(93,242,180,.4)' }}>
            ⚡ {auto.done}/{auto.total} APPROVED
          </span>
        ) : confirmAuto ? (
          <button onMouseUp={runAuto} data-lock="CONFIRM"
            style={{ padding: '6px 14px', borderRadius: 999, color: '#04121a', background: 'linear-gradient(180deg,#8dffd2,#43e69a)', boxShadow: '0 0 20px rgba(93,242,180,.5)', ...mono(16, '.18em'), border: 'none' }}>
            ⚡ CONFIRM {proposedCount} APPROVALS?
          </button>
        ) : (
          <button onMouseUp={() => proposedCount && setConfirmAuto(true)} data-lock="AUTO"
            style={{ ...pill(false), color: proposedCount ? '#5df2b4' : '#3f5665', borderColor: proposedCount ? 'rgba(93,242,180,.35)' : 'rgba(125,232,255,.1)' }}>
            ⚡ AUTO · APPROVE ALL {proposedCount ? `(${proposedCount})` : ''}
          </button>
        )}
      </div>

      {/* the panes */}
      {pool.map((r: any, i: number) => {
        const off = car.offsetOf(i);
        if (Math.abs(off) > 4.5) return null;
        const focused = Math.abs(off) < .4;
        const ba = beforeAfter(r);
        return (
          <div key={r.id} style={paneStyle(off, car.scrubbing)}>
            <div onMouseUp={() => car.tap(i)} role="button" data-lock={r.business?.toUpperCase() || 'OPEN'} className="w-full h-full" style={{
              borderRadius: 14, padding: 18, boxSizing: 'border-box', overflow: 'hidden', position: 'relative',
              background: 'linear-gradient(170deg,rgba(40,140,180,.08),rgba(8,18,28,.45) 55%)',
              border: `1px solid rgba(158,240,255,${focused ? .55 : .26})`,
              boxShadow: focused ? '0 0 70px -8px rgba(0,240,255,.4)' : '0 0 40px -12px rgba(0,240,255,.18)',
              transition: 'border-color .4s,box-shadow .4s',
            }}>
              <div style={{ position: 'absolute', inset: 0, background: 'repeating-linear-gradient(180deg,rgba(140,235,255,.05) 0 1px,transparent 1px 3px)', mixBlendMode: 'screen', opacity: .5, pointerEvents: 'none' }} />
              <div style={{ position: 'absolute', top: 0, left: '12%', right: '12%', height: 1, background: `linear-gradient(90deg,transparent,${CY},transparent)` }} />
              {/* side cards readable again (Boss revert); far cards still fade to keep edges clean */}
              <div style={{ opacity: Math.abs(off) >= 1.6 ? 0 : 1, transition: 'opacity .4s' }}>
              <div className="flex items-center" style={{ gap: 9 }}>
                <span style={{ ...display(26, 500), color: '#e9f9ff', textShadow: '0 0 10px rgba(0,240,255,.2)', flex: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.business}</span>
                <span style={{ ...mono(13, '.2em'), color: ST_COLOR[r.status] || '#7fa9bd', border: `1px solid color-mix(in srgb, ${ST_COLOR[r.status] || '#7fa9bd'} 45%, transparent)`, borderRadius: 999, padding: '2px 8px' }}>{(r.status || '').toUpperCase()}</span>
              </div>
              <div style={{ ...mono(14, '.24em'), color: CY, marginTop: 8 }}>{TYPE_LABEL[r.type] || r.type?.toUpperCase()}</div>
              <div style={{ ...display(25, 300), color: 'var(--uif-t2)', marginTop: 6, lineHeight: 1.45, display: '-webkit-box', WebkitBoxOrient: 'vertical', WebkitLineClamp: 3, overflow: 'hidden' }}>{r.title}</div>
              {/* KPI row */}
              <div className="flex" style={{ gap: 18, marginTop: 14 }}>
                {kpisOf(r).map(k => (
                  <div key={k.l}>
                    <div style={{ ...mono(13, '.18em'), color: '#49677a', marginBottom: 2 }}>{k.l}</div>
                    <div style={{ fontWeight: 200, fontSize: 38, color: '#aef0ff', textShadow: '0 0 14px rgba(0,240,255,.35)' }}>{k.v}</div>
                  </div>
                ))}
              </div>
              {/* before → after taste */}
              {ba.after?.title && (
                <div style={{ marginTop: 14, borderTop: '1px solid rgba(125,232,255,.12)', paddingTop: 10 }}>
                  <div style={{ ...mono(13, '.18em'), color: '#49677a' }}>PROPOSED</div>
                  <div style={{ ...display(23, 300), color: '#d7f2ff', marginTop: 4, lineHeight: 1.4, display: '-webkit-box', WebkitBoxOrient: 'vertical', WebkitLineClamp: 2, overflow: 'hidden' }}>{ba.after.title}</div>
                </div>
              )}
              <div style={{ position: 'absolute', left: 18, right: 18, bottom: 40, ...mono(14, '.12em'), color: '#49677a', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{pageOf(r).replace('https://', '')}</div>
              </div>{/* /focused-content fade */}
              <div style={{ position: 'absolute', left: 0, right: 0, bottom: 14, textAlign: 'center', ...mono(15, '.3em'), color: CY, textShadow: '0 0 12px rgba(0,240,255,.5)', opacity: focused ? 1 : 0, transition: 'opacity .4s' }}>PINCH TO FOCUS</div>
            </div>
          </div>
        );
      })}
      {loaded && pool.length === 0 && <EmptyState what="proposals" />}
      {/* counts foot */}
      <div className="absolute left-0 right-0 flex justify-center" style={{ bottom: 6, gap: 26 }}>
        {(['proposed', 'approved', 'done'] as const).map(s => (
          <span key={s} style={{ ...mono(15, '.22em'), color: '#56788c' }}>
            {s.toUpperCase()} · <span style={{ color: ST_COLOR[s] }}>{pool.filter((r: any) => r.status === s || (s === 'done' && ['applied', 'executed', 'done'].includes(r.status))).length}</span>
          </span>
        ))}
      </div>
    </div>
  );
}

/* ── FOCUS — one proposal, whole room ───────────────────────────── */
function FocusView({ rec, busy, onDecide, onBack, iaOpen, setIaOpen }: any) {
  const [showLive, setShowLive] = useState(false);
  const ba = beforeAfter(rec);
  const url = pageOf(rec);
  const e = ev0(rec);
  const imp = rec.projected_impact;
  const pane: React.CSSProperties = {
    borderRadius: 14, padding: 18, boxSizing: 'border-box', position: 'relative', overflow: 'hidden',
    background: 'linear-gradient(170deg,rgba(40,140,180,.08),rgba(8,18,28,.45) 55%)',
    border: '1px solid rgba(158,240,255,.26)', boxShadow: '0 0 40px -12px rgba(0,240,255,.18)',
  };
  const evRows: [string, string][] = [];
  if (e.impressions != null) evRows.push(['IMPRESSIONS · 28D', fmtN(e.impressions)]);
  if (e.clicks != null) evRows.push(['CLICKS', fmtN(e.clicks)]);
  if (e.avg_position != null) evRows.push(['AVG POSITION', e.avg_position.toFixed(1)]);
  if (e.ctr != null) evRows.push(['CTR', fmtPct(e.ctr)]);
  if (e.expected_ctr != null) evRows.push(['EXPECTED CTR', fmtPct(e.expected_ctr)]);
  if (e.missed_clicks != null) evRows.push(['MISSED CLICKS / MO', fmtN(e.missed_clicks)]);

  return (
    <div className="absolute inset-0 flex flex-col" style={{ gap: 14 }}>
      {/* focus header */}
      <div className="flex items-center" style={{ gap: 14 }}>
        <button onMouseUp={onBack} data-lock="ALL CARDS"
          style={{ ...mono(16, '.22em'), color: CY, border: '1px solid rgba(125,232,255,.25)', borderRadius: 999, padding: '7px 15px', background: 'transparent' }}>
          ‹ ALL CARDS
        </button>
        <span style={{ ...display(38, 300), color: '#e9f9ff', textShadow: '0 0 18px rgba(0,240,255,.25)' }}>{rec.business}</span>
        <span style={{ ...mono(15, '.24em'), color: CY }}>{TYPE_LABEL[rec.type] || rec.type?.toUpperCase()}</span>
        <span style={{ ...mono(15, '.12em'), color: '#56788c', flex: 1, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{url.replace('https://', '')}</span>
        <span style={{ ...mono(14, '.2em'), color: ST_COLOR[rec.status] || '#7fa9bd', border: `1px solid color-mix(in srgb, ${ST_COLOR[rec.status] || '#7fa9bd'} 45%, transparent)`, borderRadius: 999, padding: '3px 10px' }}>{(rec.status || '').toUpperCase()}</span>
      </div>

      <div className="flex-1 min-h-0 grid" style={{ gridTemplateColumns: '340px 1fr 320px', gap: 14 }}>
        {/* WHY */}
        <div style={pane} className="flex flex-col min-h-0">
          <div style={{ ...mono(16, '.3em'), color: 'var(--uif-t4)', marginBottom: 12 }}>WHY THIS PROPOSAL</div>
          <div style={{ ...display(25, 300), color: 'var(--uif-t-body)', lineHeight: 1.65 }}>{rationaleOf(rec) || rec.title}</div>
          {evRows.length > 0 && (
            <div style={{ marginTop: 16, borderTop: '1px solid rgba(125,232,255,.12)', paddingTop: 12 }}>
              <div style={{ ...mono(14, '.26em'), color: '#49677a', marginBottom: 10 }}>EVIDENCE · {e.window?.toUpperCase() || 'LAST 28 DAYS'}</div>
              {evRows.map(([l, v]) => (
                <div key={l} className="flex items-baseline justify-between" style={{ padding: '4px 0' }}>
                  <span style={{ ...mono(15, '.16em'), color: '#7fa9bd' }}>{l}</span>
                  <span style={{ fontWeight: 200, fontSize: 34, color: '#aef0ff', textShadow: '0 0 12px rgba(0,240,255,.3)' }}>{v}</span>
                </div>
              ))}
            </div>
          )}
          {e.top_pages?.length > 0 && (
            <div style={{ marginTop: 12, overflow: 'auto', minHeight: 0 }}>
              <div style={{ ...mono(14, '.26em'), color: '#49677a', marginBottom: 8 }}>NEIGHBOUR PAGES</div>
              {e.top_pages.slice(0, 5).map((p: any) => (
                <div key={p.page} className="flex justify-between" style={{ padding: '3px 0', gap: 8 }}>
                  <span style={{ ...mono(14, '.06em'), color: '#7fa9bd', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{p.page.replace(/https?:\/\/[^/]+/, '')}</span>
                  <span style={{ ...mono(15), color: '#56788c', flexShrink: 0 }}>POS {Number(p.position).toFixed(0)}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* PREVIEW */}
        <div style={pane} className="flex flex-col min-h-0">
          <div className="flex items-center" style={{ gap: 12, marginBottom: 12 }}>
            <div style={{ ...mono(16, '.3em'), color: 'var(--uif-t4)', flex: 1 }}>PREVIEW</div>
            <button onMouseUp={() => setShowLive(false)} data-lock="SERP" style={{ ...mono(14, '.18em'), color: !showLive ? '#04121a' : '#56788c', background: !showLive ? 'linear-gradient(180deg,#a8eeff,#5fd4ef)' : 'transparent', border: !showLive ? 'none' : '1px solid rgba(125,232,255,.16)', borderRadius: 999, padding: '4px 12px' }}>SERP</button>
            <button onMouseUp={() => setShowLive(true)} data-lock="LIVE PAGE" style={{ ...mono(14, '.18em'), color: showLive ? '#04121a' : '#56788c', background: showLive ? 'linear-gradient(180deg,#a8eeff,#5fd4ef)' : 'transparent', border: showLive ? 'none' : '1px solid rgba(125,232,255,.16)', borderRadius: 999, padding: '4px 12px' }}>LIVE PAGE</button>
            <a href={url} target="_blank" rel="noreferrer" data-lock="OPEN SITE" style={{ ...mono(14, '.18em'), color: CY, textDecoration: 'none' }}>OPEN ↗</a>
          </div>
          {showLive ? (
            <iframe src={`/api/improve/preview?url=${encodeURIComponent(url)}`} title="live page" className="flex-1 min-h-0 w-full" style={{ border: '1px solid rgba(125,232,255,.2)', borderRadius: 10, background: '#fff' }} />
          ) : (
            <div className="flex-1 min-h-0 overflow-auto flex flex-col" style={{ gap: 12 }}>
              {(ba.before?.title || ba.after?.title) ? (
                <>
                  <Serp label={rec.type === 'ab_promote' ? 'VARIANT A · CURRENT' : 'CURRENT RESULT'} url={url} title={ba.before?.title} meta={ba.before?.meta} />
                  <div style={{ textAlign: 'center', ...mono(18), color: CY }}>▼</div>
                  <Serp label={rec.type === 'ab_promote' ? 'VARIANT B · PROPOSED' : 'PROPOSED RESULT'} url={url} title={ba.after?.title} meta={ba.after?.meta} glow />
                </>
              ) : (
                <div style={{ ...display(24, 300), color: 'var(--uif-t-body)', lineHeight: 1.7 }}>
                  {Object.entries(rec.proposal || {}).filter(([k, v]) => typeof v === 'string' && k !== 'site').map(([k, v]) => (
                    <div key={k} style={{ marginBottom: 12 }}>
                      <div style={{ ...mono(14, '.24em'), color: '#49677a', marginBottom: 4 }}>{k.replace(/_/g, ' ').toUpperCase()}</div>
                      <div>{v as string}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* DECIDE */}
        <div style={pane} className="flex flex-col">
          <div style={{ ...mono(16, '.3em'), color: 'var(--uif-t4)', marginBottom: 14 }}>DECIDE</div>
          {imp && (imp.current || imp.expected) ? (
            <div style={{ marginBottom: 18 }}>
              <div style={{ ...mono(14, '.24em'), color: '#49677a', marginBottom: 6 }}>PROJECTED {String(imp.metric || '').toUpperCase()}</div>
              <div className="flex items-baseline" style={{ gap: 10 }}>
                <span style={{ fontWeight: 200, fontSize: 52, color: '#7fa9bd' }}>{fmtPct(imp.current ?? 0)}</span>
                <span style={{ ...mono(20), color: CY }}>→</span>
                <span style={{ fontWeight: 200, fontSize: 52, color: '#5df2b4', textShadow: '0 0 16px rgba(93,242,180,.4)' }}>{fmtPct(imp.expected ?? 0)}</span>
              </div>
              {imp.confidence && <div style={{ ...mono(14, '.2em'), color: '#56788c', marginTop: 4 }}>CONFIDENCE · {String(imp.confidence).toUpperCase()}</div>}
            </div>
          ) : imp?.metric ? (
            <div style={{ marginBottom: 18, ...mono(15, '.2em'), color: '#7fa9bd' }}>METRIC · {String(imp.metric).toUpperCase()}{imp.confidence ? ` · ${String(imp.confidence).toUpperCase()} CONFIDENCE` : ''}</div>
          ) : null}
          {rec.status === 'proposed' ? (
            <>
              <button onMouseUp={() => onDecide(rec, 'approve')} disabled={busy} data-lock="APPROVE"
                style={{ width: '100%', padding: '16px 0', borderRadius: 12, border: 'none', color: '#04121a', background: 'linear-gradient(180deg,#8dffd2,#43e69a)', boxShadow: '0 0 30px rgba(93,242,180,.45)', ...mono(20, '.3em'), opacity: busy ? .5 : 1, transition: 'opacity .2s' }}>
                {busy ? 'APPROVING…' : '✓ APPROVE'}
              </button>
              <button onMouseUp={() => onDecide(rec, 'reject')} disabled={busy} data-lock="REJECT"
                style={{ width: '100%', padding: '10px 0', marginTop: 10, borderRadius: 12, background: 'transparent', border: '1px solid rgba(255,125,138,.35)', color: '#ff7d8a', ...mono(16, '.26em'), opacity: busy ? .5 : 1 }}>
                ✕ REJECT
              </button>
            </>
          ) : (
            <div style={{ padding: '14px 0', textAlign: 'center', borderRadius: 12, border: `1px solid color-mix(in srgb, ${ST_COLOR[rec.status] || '#7fa9bd'} 40%, transparent)`, color: ST_COLOR[rec.status] || '#7fa9bd', ...mono(18, '.26em') }}>
              {(rec.status || '').toUpperCase()}{rec.decided_at ? ` · ${new Date(rec.decided_at).toLocaleDateString()}` : ''}
            </div>
          )}
          {rec.execution && (
            <div style={{ marginTop: 14, borderTop: '1px solid rgba(125,232,255,.12)', paddingTop: 10 }}>
              <div style={{ ...mono(14, '.24em'), color: '#49677a', marginBottom: 6 }}>EXECUTION</div>
              <div style={{ ...mono(16, '.14em'), color: '#5df2b4' }}>{rec.execution.status?.toUpperCase()} · {rec.execution.executor?.toUpperCase() || ''}</div>
            </div>
          )}
          <div style={{ flex: 1 }} />
          <button onMouseUp={() => setIaOpen(!iaOpen)} data-lock="ASK WHY"
            style={{ width: '100%', padding: '12px 0', borderRadius: 12, background: iaOpen ? 'linear-gradient(180deg,#a8eeff,#5fd4ef)' : 'transparent', color: iaOpen ? '#04121a' : CY, border: iaOpen ? 'none' : '1px solid rgba(125,232,255,.3)', ...mono(17, '.24em') }}>
            ◈ ASK THE ASSISTANT
          </button>
          <div style={{ ...mono(13, '.14em'), color: '#3d4654', marginTop: 8, textAlign: 'center' }}>WHY IS THIS PROPOSED? · WHAT'S THE RISK?</div>
        </div>
      </div>
    </div>
  );
}
